import { FormHelperText, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { memo, useCallback } from "react";
import {
  TIntegrationRuleMovForm,
  getIntegrationRuleMovSchema,
} from "./integrationRuleMovSchema";
import {
  FormContainer,
  FormInput,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { IIntegrationRuleMovProps } from "./models";
import {
  createIntegrationRuleMov,
  getIntegrationRuleMovById,
  updateIntegrationRuleMovById,
} from "../../../../../../../services/integrationRuleMov.service";
import { InternalProcedureClassification } from "./InternalProcedureClassification";
import { ProcedureType } from "./ProcedureType";
import { EstablishmentExternalId } from "./EstablishmentExternalId";
import { ProviderExternalId } from "./ProviderExternalId";
import { EstablishmentExternalIdThen } from "./EstablishmentExternalIdThen";
import { PatientEncounterTypeExternalIdThen } from "./PatientEncounterTypeExternalIdThen";
import { TissAttendanceTypeExternalIdThen } from "./TissAttendanceTypeExternalIdThen";
import { PatientEncounterOriginExternalIdThen } from "./PatientEncounterOriginExternalIdThen";
import { PatientCareCharacterExternalIdThen } from "./PatientCareCharacterExternalIdThen";
import { PatientCareClassificationExternalIdThen } from "./PatientCareClassificationExternalIdThen";
import { InsuranceTypeExternalIdThen } from "./InsuranceTypeExternalIdThen";
import { InsuranceExternalIdThen } from "./InsuranceExternalIdThen";
import { InsuranceCategoryExternalIdThen } from "./InsuranceCategoryExternalIdThen";
import { InsurancePlanExternalIdThen } from "./InsurancePlanExternalIdThen";
import { SectorExternalIdThen } from "./SectorExternalIdThen";
import { SectorAccommodationTypeExternalIdThen } from "./SectorAccommodationTypeExternalIdThen";
import { Insurance } from "./Insurance";
import { Sector } from "./Sector";
import { ProcedureArea } from "./ProcedureArea";
import { ProcedureSpecialty } from "./ProcedureSpecialty";
import { ProcedureGroup } from "./ProcedureGroup";
import { LabExamClassification } from "./LabExamClassification";
import { PlanOperationType } from "./PlanOperationType";
import { PlanPriceFormation } from "./PlanPriceFormation";
import { InternalProcedure } from "./InternalProcedure";
import { InternalProcedureThen } from "./InternalProcedureThen";
import { ScheduleThen } from "./ScheduleThen";
import { DefaultDoctorExternalIdThen } from "./DefaultDoctorExternalIdThen";
import { DoctorObtainMode } from "./DoctorObtainMode";
import { PhaseHeader } from "./PhaseHeader";
import { SectorUnitFields } from "./SectorUnitFields";

const translationPath = "page.4uBus.integrationRule.integrationRuleMov.";

// Automation code for "Tasy OPS - Gerar Atendimento e Prescrição da OPS -
// Autorização (Execução Requisição)" (#2936) — only this automation shows
// the extra external-id fields added in #2947.
const TASY_OPS_AUTOMATION_CODE = 19;
// Automation code for "TASY EMR - Gerar novo Atendimento no PS, a partir de
// inelegibilidade de atendimento do PA" (#3087) — HVS UPA->PS flow (#3088),
// shows its own field set, distinct from the Itumbiara (Tasy Ops) one above.
const HVS_UPA_PS_AUTOMATION_CODE = 21;

export const IntegrationRuleMov = memo(
  ({
    id,
    busIntegrationRuleId,
    busAutomationCode,
    onBackButton,
  }: IIntegrationRuleMovProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createIntegrationRuleMov,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateIntegrationRuleMovById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getIntegrationRuleMovById,
    );

    const isTasyOps = busAutomationCode === TASY_OPS_AUTOMATION_CODE;
    const isHvsUpaPs = busAutomationCode === HVS_UPA_PS_AUTOMATION_CODE;

    const saveHandler = (v: TIntegrationRuleMovForm) =>
      save({
        ...v,
        _4uBusIntegrationRuleId: busIntegrationRuleId ?? "",
        establishmentExternalId: v.establishmentExternalId?.id,
        establishmentExternalIdName: v.establishmentExternalId?.name,
        providerExternalId: v.providerExternalId?.id,
        providerExternalIdName: v.providerExternalId?.name,
        establishmentExternalIdThen: v.establishmentExternalIdThen?.id,
        establishmentExternalIdThenName: v.establishmentExternalIdThen?.name,
        patientEncounterTypeExternalIdThen:
          v.patientEncounterTypeExternalIdThen?.id,
        patientEncounterTypeExternalIdThenName:
          v.patientEncounterTypeExternalIdThen?.name,
        patientEncounterOriginExternalIdThen:
          v.patientEncounterOriginExternalIdThen?.id,
        patientEncounterOriginExternalIdThenName:
          v.patientEncounterOriginExternalIdThen?.name,
        patientCareCharacterExternalIdThen:
          v.patientCareCharacterExternalIdThen?.id,
        patientCareCharacterExternalIdThenName:
          v.patientCareCharacterExternalIdThen?.name,
        patientCareClassificationExternalIdThen:
          v.patientCareClassificationExternalIdThen?.id,
        patientCareClassificationExternalIdThenName:
          v.patientCareClassificationExternalIdThen?.name,
        insuranceExternalIdThen: v.insuranceExternalIdThen?.id,
        insuranceExternalIdThenName: v.insuranceExternalIdThen?.name,
        insuranceCategoryExternalIdThen: v.insuranceCategoryExternalIdThen?.id,
        insuranceCategoryExternalIdThenName:
          v.insuranceCategoryExternalIdThen?.name,
        sectorExternalIdThen: v.sectorExternalIdThen?.id,
        sectorExternalIdThenName: v.sectorExternalIdThen?.name,
        sectorAccommodationTypeExternalIdThen:
          v.sectorAccommodationTypeExternalIdThen?.id,
        sectorAccommodationTypeExternalIdThenName:
          v.sectorAccommodationTypeExternalIdThen?.name,
        tissAttendanceTypeExternalIdThen:
          v.tissAttendanceTypeExternalIdThen?.id,
        tissAttendanceTypeExternalIdThenName:
          v.tissAttendanceTypeExternalIdThen?.name,
        insuranceTypeExternalIdThen: v.insuranceTypeExternalIdThen?.id,
        insuranceTypeExternalIdThenName:
          v.insuranceTypeExternalIdThen?.name,
        insurancePlanExternalIdThen: v.insurancePlanExternalIdThen?.id,
        insurancePlanExternalIdThenName:
          v.insurancePlanExternalIdThen?.name,
        _4uBusIntegrationRuleMovIProcedClasses:
          v.uBusIntegrationRuleMovIProcedClasses?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovProcedTypes:
          v.uBusIntegrationRuleMovProcedTypes?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        verifyReleasedOpinion: v.verifyReleasedOpinion ?? false,
        doctorObtainMode: v.doctorObtainMode ?? "SCHEDULE",
        defaultDoctorExternalIdThen: v.defaultDoctorExternalIdThen?.id,
        defaultDoctorExternalIdThenName: v.defaultDoctorExternalIdThen?.name,
        _4uBusIntegrationRuleMovInsurances: v.uBusIntegrationRuleMovInsurances?.map(
          (item) => ({ externalId: item.id, externalName: item.name }),
        ),
        _4uBusIntegrationRuleMovSectors: v.uBusIntegrationRuleMovSectors?.map(
          (item) => ({ externalId: item.id, externalName: item.name }),
        ),
        _4uBusIntegrationRuleMovProcedureAreas:
          v.uBusIntegrationRuleMovProcedureAreas?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovProcedureSpecialties:
          v.uBusIntegrationRuleMovProcedureSpecialties?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovProcedureGroups:
          v.uBusIntegrationRuleMovProcedureGroups?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovLabExamClassifications:
          v.uBusIntegrationRuleMovLabExamClassifications?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovPlanOperationTypes:
          v.uBusIntegrationRuleMovPlanOperationTypes?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovPlanPriceFormations:
          v.uBusIntegrationRuleMovPlanPriceFormations?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovInternalProcedures:
          v.uBusIntegrationRuleMovInternalProcedures?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovInternalProceduresThen:
          v.uBusIntegrationRuleMovInternalProceduresThen?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
        _4uBusIntegrationRuleMovSchedulesThen:
          v.uBusIntegrationRuleMovSchedulesThen?.map((item) => ({
            externalId: item.id,
            externalName: item.name,
          })),
      });

    const updateHandler = (v: TIntegrationRuleMovForm, id: string) =>
      update({
        item: {
          ...v,
          _4uBusIntegrationRuleId: busIntegrationRuleId ?? "",
          establishmentExternalId: v.establishmentExternalId?.id,
          establishmentExternalIdName: v.establishmentExternalId?.name,
          providerExternalId: v.providerExternalId?.id,
          providerExternalIdName: v.providerExternalId?.name,
          establishmentExternalIdThen: v.establishmentExternalIdThen?.id,
          establishmentExternalIdThenName:
            v.establishmentExternalIdThen?.name,
          patientEncounterTypeExternalIdThen:
            v.patientEncounterTypeExternalIdThen?.id,
          patientEncounterTypeExternalIdThenName:
            v.patientEncounterTypeExternalIdThen?.name,
          patientEncounterOriginExternalIdThen:
            v.patientEncounterOriginExternalIdThen?.id,
          patientEncounterOriginExternalIdThenName:
            v.patientEncounterOriginExternalIdThen?.name,
          patientCareCharacterExternalIdThen:
            v.patientCareCharacterExternalIdThen?.id,
          patientCareCharacterExternalIdThenName:
            v.patientCareCharacterExternalIdThen?.name,
          patientCareClassificationExternalIdThen:
            v.patientCareClassificationExternalIdThen?.id,
          patientCareClassificationExternalIdThenName:
            v.patientCareClassificationExternalIdThen?.name,
          insuranceExternalIdThen: v.insuranceExternalIdThen?.id,
          insuranceExternalIdThenName: v.insuranceExternalIdThen?.name,
          insuranceCategoryExternalIdThen:
            v.insuranceCategoryExternalIdThen?.id,
          insuranceCategoryExternalIdThenName:
            v.insuranceCategoryExternalIdThen?.name,
          sectorExternalIdThen: v.sectorExternalIdThen?.id,
          sectorExternalIdThenName: v.sectorExternalIdThen?.name,
          sectorAccommodationTypeExternalIdThen:
            v.sectorAccommodationTypeExternalIdThen?.id,
          sectorAccommodationTypeExternalIdThenName:
            v.sectorAccommodationTypeExternalIdThen?.name,
          tissAttendanceTypeExternalIdThen:
            v.tissAttendanceTypeExternalIdThen?.id,
          tissAttendanceTypeExternalIdThenName:
            v.tissAttendanceTypeExternalIdThen?.name,
          insuranceTypeExternalIdThen: v.insuranceTypeExternalIdThen?.id,
          insuranceTypeExternalIdThenName:
            v.insuranceTypeExternalIdThen?.name,
          insurancePlanExternalIdThen: v.insurancePlanExternalIdThen?.id,
          insurancePlanExternalIdThenName:
            v.insurancePlanExternalIdThen?.name,
          _4uBusIntegrationRuleMovIProcedClasses:
            v.uBusIntegrationRuleMovIProcedClasses?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovProcedTypes:
            v.uBusIntegrationRuleMovProcedTypes?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          verifyReleasedOpinion: v.verifyReleasedOpinion ?? false,
          doctorObtainMode: v.doctorObtainMode ?? "SCHEDULE",
          defaultDoctorExternalIdThen: v.defaultDoctorExternalIdThen?.id,
          defaultDoctorExternalIdThenName:
            v.defaultDoctorExternalIdThen?.name,
          _4uBusIntegrationRuleMovInsurances:
            v.uBusIntegrationRuleMovInsurances?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovSectors:
            v.uBusIntegrationRuleMovSectors?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovProcedureAreas:
            v.uBusIntegrationRuleMovProcedureAreas?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovProcedureSpecialties:
            v.uBusIntegrationRuleMovProcedureSpecialties?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovProcedureGroups:
            v.uBusIntegrationRuleMovProcedureGroups?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovLabExamClassifications:
            v.uBusIntegrationRuleMovLabExamClassifications?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovPlanOperationTypes:
            v.uBusIntegrationRuleMovPlanOperationTypes?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovPlanPriceFormations:
            v.uBusIntegrationRuleMovPlanPriceFormations?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovInternalProcedures:
            v.uBusIntegrationRuleMovInternalProcedures?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovInternalProceduresThen:
            v.uBusIntegrationRuleMovInternalProceduresThen?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
          _4uBusIntegrationRuleMovSchedulesThen:
            v.uBusIntegrationRuleMovSchedulesThen?.map((item) => ({
              externalId: item.id,
              externalName: item.name,
            })),
        },
        id: id,
      });

    const getHandler = useCallback(
      async (id: string): Promise<Treturn<TIntegrationRuleMovForm>> => {
        const { data, success } = await get(id);
        if (data && success) {
          const newData: TIntegrationRuleMovForm = {
            ...data,
            establishmentExternalId: data.establishmentExternalId
              ? {
                  id: data.establishmentExternalId,
                  name: data.establishmentExternalIdName ?? data.establishmentExternalId,
                }
              : null,
            providerExternalId: data.providerExternalId
              ? {
                  id: data.providerExternalId,
                  name: data.providerExternalIdName ?? data.providerExternalId,
                }
              : null,
            establishmentExternalIdThen: data.establishmentExternalIdThen
              ? {
                  id: data.establishmentExternalIdThen,
                  name:
                    data.establishmentExternalIdThenName ??
                    data.establishmentExternalIdThen,
                }
              : null,
            patientEncounterTypeExternalIdThen:
              data.patientEncounterTypeExternalIdThen
                ? {
                    id: data.patientEncounterTypeExternalIdThen,
                    name:
                      data.patientEncounterTypeExternalIdThenName ??
                      data.patientEncounterTypeExternalIdThen,
                  }
                : null,
            patientEncounterOriginExternalIdThen:
              data.patientEncounterOriginExternalIdThen
                ? {
                    id: data.patientEncounterOriginExternalIdThen,
                    name:
                      data.patientEncounterOriginExternalIdThenName ??
                      data.patientEncounterOriginExternalIdThen,
                  }
                : null,
            patientCareCharacterExternalIdThen:
              data.patientCareCharacterExternalIdThen
                ? {
                    id: data.patientCareCharacterExternalIdThen,
                    name:
                      data.patientCareCharacterExternalIdThenName ??
                      data.patientCareCharacterExternalIdThen,
                  }
                : null,
            patientCareClassificationExternalIdThen:
              data.patientCareClassificationExternalIdThen
                ? {
                    id: data.patientCareClassificationExternalIdThen,
                    name:
                      data.patientCareClassificationExternalIdThenName ??
                      data.patientCareClassificationExternalIdThen,
                  }
                : null,
            insuranceExternalIdThen: data.insuranceExternalIdThen
              ? {
                  id: data.insuranceExternalIdThen,
                  name:
                    data.insuranceExternalIdThenName ??
                    data.insuranceExternalIdThen,
                }
              : null,
            insuranceCategoryExternalIdThen: data.insuranceCategoryExternalIdThen
              ? {
                  id: data.insuranceCategoryExternalIdThen,
                  name:
                    data.insuranceCategoryExternalIdThenName ??
                    data.insuranceCategoryExternalIdThen,
                }
              : null,
            sectorExternalIdThen: data.sectorExternalIdThen
              ? {
                  id: data.sectorExternalIdThen,
                  name:
                    data.sectorExternalIdThenName ?? data.sectorExternalIdThen,
                }
              : null,
            sectorAccommodationTypeExternalIdThen:
              data.sectorAccommodationTypeExternalIdThen
                ? {
                    id: data.sectorAccommodationTypeExternalIdThen,
                    name:
                      data.sectorAccommodationTypeExternalIdThenName ??
                      data.sectorAccommodationTypeExternalIdThen,
                  }
                : null,
            tissAttendanceTypeExternalIdThen:
              data.tissAttendanceTypeExternalIdThen
                ? {
                    id: data.tissAttendanceTypeExternalIdThen,
                    name:
                      data.tissAttendanceTypeExternalIdThenName ??
                      data.tissAttendanceTypeExternalIdThen,
                  }
                : null,
            insuranceTypeExternalIdThen: data.insuranceTypeExternalIdThen
              ? {
                  id: data.insuranceTypeExternalIdThen,
                  name:
                    data.insuranceTypeExternalIdThenName ??
                    data.insuranceTypeExternalIdThen,
                }
              : null,
            insurancePlanExternalIdThen: data.insurancePlanExternalIdThen
              ? {
                  id: data.insurancePlanExternalIdThen,
                  name:
                    data.insurancePlanExternalIdThenName ??
                    data.insurancePlanExternalIdThen,
                }
              : null,
            uBusIntegrationRuleMovIProcedClasses:
              data._4uBusIntegrationRuleMovIProcedClasses?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovProcedTypes:
              data._4uBusIntegrationRuleMovProcedTypes?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            defaultDoctorExternalIdThen: data.defaultDoctorExternalIdThen
              ? {
                  id: data.defaultDoctorExternalIdThen,
                  name:
                    data.defaultDoctorExternalIdThenName ??
                    data.defaultDoctorExternalIdThen,
                }
              : null,
            uBusIntegrationRuleMovInsurances:
              data._4uBusIntegrationRuleMovInsurances?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovSectors:
              data._4uBusIntegrationRuleMovSectors?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovProcedureAreas:
              data._4uBusIntegrationRuleMovProcedureAreas?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovProcedureSpecialties:
              data._4uBusIntegrationRuleMovProcedureSpecialties?.map(
                (item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }),
              ),
            uBusIntegrationRuleMovProcedureGroups:
              data._4uBusIntegrationRuleMovProcedureGroups?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovLabExamClassifications:
              data._4uBusIntegrationRuleMovLabExamClassifications?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovPlanOperationTypes:
              data._4uBusIntegrationRuleMovPlanOperationTypes?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovPlanPriceFormations:
              data._4uBusIntegrationRuleMovPlanPriceFormations?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
            uBusIntegrationRuleMovInternalProcedures:
              data._4uBusIntegrationRuleMovInternalProcedures?.map(
                (item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }),
              ),
            uBusIntegrationRuleMovInternalProceduresThen:
              data._4uBusIntegrationRuleMovInternalProceduresThen?.map(
                (item) => ({
                  id: item.externalId,
                  name: item.externalName,
                }),
              ),
            uBusIntegrationRuleMovSchedulesThen:
              data._4uBusIntegrationRuleMovSchedulesThen?.map((item) => ({
                id: item.externalId,
                name: item.externalName,
              })),
          };
          return { data: newData, success: true };
        }

        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TIntegrationRuleMovForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "integration_rule_mov")}
        schema={getIntegrationRuleMovSchema(isTasyOps, isHvsUpaPs)}
        subtitle={t(translationPath + "new_integration_rule_mov")}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} textAlign="end">
              <SwitchInput
                name="enable"
                label={t(translationPath + "enable")}
              />
            </Grid>

            {isTasyOps && (
              <>
                <Grid item sm={12}>
                  <Typography fontWeight="bold" color="error">
                    {t(translationPath + "tasy_ops_if_label")}
                  </Typography>
                </Grid>

                <EstablishmentExternalId />
                <ProviderExternalId />
                <PlanOperationType />
                <PlanPriceFormation />
                <Grid item xs={12}>
                  <FormHelperText>
                    {t(translationPath + "plan_filters_message_helper")}
                  </FormHelperText>
                </Grid>
              </>
            )}

            {isHvsUpaPs && (
              <>
                <Grid item sm={12}>
                  <Typography fontWeight="bold" color="error">
                    {t(translationPath + "hvs_upa_ps_if_label")}
                  </Typography>
                </Grid>

                <PhaseHeader
                  titleKey="hvs_upa_ps_job1_label"
                  descriptionKey="hvs_upa_ps_job1_description"
                />

                <Insurance />
                <Sector />

                <Grid item xs={12} sm={6}>
                  <SwitchInput
                    name="verifyReleasedOpinion"
                    label={t(translationPath + "verify_released_opinion")}
                  />
                </Grid>
              </>
            )}

            <InternalProcedureClassification />

            <ProcedureType />

            {isHvsUpaPs && (
              <>
                <ProcedureArea />
                <ProcedureSpecialty />
                <ProcedureGroup />
                <InternalProcedure />
                <LabExamClassification />
              </>
            )}

            <Grid item xs={12} sm={12}>
              <FormInput
                size="small"
                name="individualExternalId"
                fullWidth
                label={t(translationPath + "individual_external_id")}
              />
              <FormHelperText>
                {t(translationPath + "individual_identifier_message_helper")}
              </FormHelperText>
            </Grid>

            {isTasyOps && (
              <>
                <Grid item sm={12}>
                  <Typography fontWeight="bold" color="error">
                    {t(translationPath + "tasy_ops_then_label")}
                  </Typography>
                </Grid>

                <EstablishmentExternalIdThen />
                <PatientEncounterTypeExternalIdThen />
                <TissAttendanceTypeExternalIdThen />
                <PatientEncounterOriginExternalIdThen />
                <PatientCareCharacterExternalIdThen />
                <PatientCareClassificationExternalIdThen />
                <InsuranceTypeExternalIdThen />
                <InsuranceExternalIdThen />
                <InsuranceCategoryExternalIdThen />
                <InsurancePlanExternalIdThen />
                <SectorExternalIdThen />
                <SectorAccommodationTypeExternalIdThen />
              </>
            )}

            {isHvsUpaPs && (
              <>
                <Grid item sm={12}>
                  <Typography fontWeight="bold" color="error">
                    {t(translationPath + "hvs_upa_ps_then_label")}
                  </Typography>
                </Grid>

                <PhaseHeader
                  titleKey="hvs_upa_ps_job2_phase1_label"
                  descriptionKey="hvs_upa_ps_job2_phase1_description"
                />

                <InternalProcedureThen />

                <PhaseHeader
                  titleKey="hvs_upa_ps_job2_phase2_label"
                  descriptionKey="hvs_upa_ps_job2_phase2_description"
                />

                <DoctorObtainMode />
                <ScheduleThen />
                <DefaultDoctorExternalIdThen />
                <SectorExternalIdThen />
                <SectorUnitFields />
                <TissAttendanceTypeExternalIdThen />
              </>
            )}

            {isTasyOps && <SectorUnitFields />}
          </Grid>
        )}
      />
    );
  },
);
